import { useCallback } from 'react';
import adyenUtils from 'adyenthreeds2-js-utils/dist/threeds2-js-utils';
import { CardValidationError } from '../../../constants';
import { Psd2AuthData } from '../usePaymentMethodsQuery';
import { getContainerElement } from './getContainerElement';
import { useChallengeShopper } from './useChallengeShopper';

const IFRAME_NAME = 'threeDSMethodIframe';

const convertApiDataToPsd2AuthData = (apiData?: { [key: string]: string }): Psd2AuthData | null => {
  if (!apiData) return null;

  return {
    __typename: 'RestPsd2AuthData',
    threeDSServerTransID: apiData['threeds2.threeDSServerTransID'],
    threeDSMethodURL: apiData['threeds2.threeDSMethodURL'],
    acsURL: apiData['threeds2.threeDS2ResponseData.acsURL'],
    acsTransID: apiData['threeds2.threeDS2ResponseData.acsTransID'],
    messageVersion: apiData['threeds2.threeDS2ResponseData.messageVersion'],
    threeDSResponseServerTransID: apiData['threeds2.threeDS2ResponseData.threeDSServerTransID'],
    threeDSMethodNotificationURL: apiData.threeds_method_notification_url,
    issuerUrl: apiData.issuer_url,
    md: apiData.md,
    paRequest: apiData.pa_request,
    termUrl: apiData.term_url
  };
};

export const useIdentifyShopper = () => {
  const challengeShopper = useChallengeShopper();

  const identifyShopper = useCallback(
    (psd2AuthData?: Psd2AuthData | null) =>
      new Promise<void>((resolve, reject) => {
        if (!psd2AuthData)
          return reject(new CardValidationError('identifyShopper: psd2AuthData missing'));

        const {
          threeDSServerTransID,
          threeDSMethodURL,
          threeDSMethodNotificationURL
        } = psd2AuthData;
        const container = getContainerElement();

        const onMessage = (event: MessageEvent) => {
          if (event.data.type === 'IdentifyShopper') {
            window.removeEventListener('message', onMessage);

            const resultCode = event.data.data?.attributes?.result_code;

            if (resultCode === 'Authorised') return resolve();

            if (resultCode === 'ChallengeShopper')
              return challengeShopper(
                convertApiDataToPsd2AuthData(event.data.data?.attributes?.psd2_auth_data)
              )
                .then(resolve)
                .catch(reject);

            if (resultCode === 'Refused')
              return reject(new CardValidationError('identifyShopper: authorization refused'));

            const error = new CardValidationError('identifyShopper: unknown resultCode');
            error.networkError = { result: event.data };

            reject(error);
          }
        };

        window.addEventListener('message', onMessage);

        adyenUtils.createIframe(container, IFRAME_NAME);

        const form = adyenUtils.createForm(
          'threedsMethodForm',
          threeDSMethodURL,
          IFRAME_NAME,
          'threeDSMethodData',
          adyenUtils.base64Url.encode(
            JSON.stringify({ threeDSServerTransID, threeDSMethodNotificationURL })
          )
        );

        container.appendChild(form);
        form.submit();
        container.removeChild(form);
      }),
    [challengeShopper]
  );

  return identifyShopper;
};
